import { Navigate, Outlet, useLocation } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import { useSupabase } from "./SupabaseProvider";

const ALLOWED_DOMAIN = "@prospect.solutions";

/**
 * Route guard. Three states:
 *
 * 1. **Loading initial session** — render nothing for a beat. Avoids the
 *    flash of `/sign-in` on tabs that already have a session in
 *    localStorage.
 * 2. **No session** — redirect to `/sign-in?next=<currentPath>`. The
 *    `next` round-trip preserves deep links (`/jobs/abc` → sign-in →
 *    back to `/jobs/abc`, not `/`).
 * 3. **Session with non-prospect.solutions email** — render a full-page
 *    Alert. The orchestrator JWT verifier (PR1) is the authoritative
 *    gate: any direct API call still returns 401. This is a UX
 *    guardrail so the user sees *why* nothing loads.
 *
 * Otherwise renders the matched child route via `<Outlet />`.
 */
export function AuthGate() {
  const { session, client, loading } = useSupabase();
  const location = useLocation();

  if (loading) return null;

  if (!session) {
    const next = location.pathname + location.search;
    const search = next === "/" ? "" : `?next=${encodeURIComponent(next)}`;
    return <Navigate to={`/sign-in${search}`} replace />;
  }

  const email = session.user.email ?? "";
  if (!email.endsWith(ALLOWED_DOMAIN)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-lg">
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              The admin UI is restricted to <span className="font-mono">{ALLOWED_DOMAIN}</span>{" "}
              accounts. You're signed in as{" "}
              <span className="font-mono">{email || "(unknown)"}</span>.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                void client.auth.signOut();
              }}
            >
              Sign out
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <Outlet />;
}
